import { settings } from '../core/settings';
import { audioOut } from './sfx';

/**
 * Looping weather beds made of filtered noise (no audio files): steady rain,
 * a low roar for downpours, and gusting typhoon wind. Everything goes through
 * the sfx master bus, so the mute switch (`applyMute`) silences it too. Indoors
 * the beds are low-passed and quieter, like rain heard through the windows.
 */
interface Beds {
  ctx: AudioContext;
  rainLp: BiquadFilterNode;
  rainGain: GainNode;
  roarGain: GainNode;
  windBp: BiquadFilterNode;
  windGain: GainNode;
}
let beds: Beds | null = null;
let gustT = 0;

function loopNoise(ctx: AudioContext, seconds: number, brown: boolean): AudioBufferSourceNode {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (brown) {
      last = (last + 0.04 * w) / 1.04;
      d[i] = last * 3.5;
    } else d[i] = w;
  }
  // Cross-fade the loop seam so there's no click every few seconds.
  const fade = Math.floor(ctx.sampleRate * 0.05);
  for (let i = 0; i < fade; i++) {
    const k = i / fade;
    d[i] = d[i] * k + d[len - fade + i] * (1 - k);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.loopEnd = (len - fade) / ctx.sampleRate;
  return src;
}

function build(): Beds | null {
  const io = audioOut();
  if (!io) return null;
  const { ctx, out } = io;
  // Rain: bright hiss of drops.
  const rain = loopNoise(ctx, 3, false);
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 500;
  const rainLp = ctx.createBiquadFilter();
  rainLp.type = 'lowpass';
  rainLp.frequency.value = 5200;
  const rainGain = ctx.createGain();
  rainGain.gain.value = 0;
  rain.connect(hp).connect(rainLp).connect(rainGain).connect(out);
  // Downpour roar: the low body of heavy rain.
  const roar = loopNoise(ctx, 4, true);
  const roarLp = ctx.createBiquadFilter();
  roarLp.type = 'lowpass';
  roarLp.frequency.value = 420;
  const roarGain = ctx.createGain();
  roarGain.gain.value = 0;
  roar.connect(roarLp).connect(roarGain).connect(out);
  // Wind: band-passed noise whose pitch and level follow the gusts.
  const wind = loopNoise(ctx, 5, true);
  const windBp = ctx.createBiquadFilter();
  windBp.type = 'bandpass';
  windBp.frequency.value = 400;
  windBp.Q.value = 1.6;
  const windGain = ctx.createGain();
  windGain.gain.value = 0;
  wind.connect(windBp).connect(windGain).connect(out);
  rain.start();
  roar.start();
  wind.start();
  return { ctx, rainLp, rainGain, roarGain, windBp, windGain };
}

/**
 * Called every frame. `rain` and `wind` are 0..1; `indoor` muffles both.
 * Builds the audio graph lazily, the first time there's something to hear.
 */
export function updateAmbience(dt: number, rain: number, wind: number, indoor: boolean): void {
  const audible = settings.sound && (rain > 0.01 || wind > 0.5);
  if (!beds) {
    if (!audible) return;
    beds = build();
    if (!beds) return;
  }
  const { ctx } = beds;
  if (ctx.state !== 'running') return;
  const t = ctx.currentTime;
  const muffle = indoor ? 0.32 : 1;
  const r = settings.sound ? rain : 0;
  beds.rainGain.gain.setTargetAtTime(0.11 * Math.sqrt(r) * muffle, t, 0.4);
  beds.roarGain.gain.setTargetAtTime(0.14 * Math.max(0, r - 0.5) * 2 * muffle, t, 0.5);
  beds.rainLp.frequency.setTargetAtTime(indoor ? 750 : 3200 + r * 2600, t, 0.3);
  // Gusts: slow random swells, stronger and higher outdoors.
  gustT += dt;
  const gust = 0.55 + 0.45 * Math.sin(gustT * 0.7) * Math.sin(gustT * 0.31 + 1.3);
  const w = settings.sound && wind > 0.5 ? (wind - 0.5) * 2 : 0;
  beds.windGain.gain.setTargetAtTime(0.16 * w * gust * (indoor ? 0.45 : 1), t, 0.35);
  beds.windBp.frequency.setTargetAtTime((indoor ? 220 : 320) + gust * (indoor ? 160 : 520), t, 0.4);
}

/** Distant footsteps of other students running down the school corridor. */
export function distantSteps(): void {
  const io = audioOut();
  if (!io || !settings.sound || io.ctx.state !== 'running') return;
  const { ctx, out } = io;
  const n = 4 + Math.floor(Math.random() * 5);
  const pace = 0.17 + Math.random() * 0.1;
  const fadeIn = Math.random() < 0.5;
  for (let i = 0; i < n; i++) {
    const k = fadeIn ? i / n : 1 - i / n;
    const at = ctx.currentTime + 0.05 + i * pace;
    const len = Math.floor(ctx.sampleRate * 0.04);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < len; j++) d[j] = (Math.random() * 2 - 1) * (1 - j / len);
    for (const [delay, vol] of [[0, 1], [0.13, 0.4]] as const) {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 1500;
      f.Q.value = 2;
      const g = ctx.createGain();
      g.gain.value = (0.012 + 0.03 * k) * vol;
      src.connect(f).connect(g).connect(out);
      src.start(at + delay);
    }
  }
}
